import { memoizeGetUserDetails } from "./users";
import { memoizeGetRoleplay } from "./roleplays";
import { memoizeGetBot } from "./bots";
import { memoizeGetCourseSectionTopic } from "./courses";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";

const isBotExercise = (exercise) =>
  exercise === "bot" || exercise === "bot-listen-and-speak";

const gameList = [
  "roleplay-story",
  "roleplay-fill_in_blanks",
  "roleplay-act_it_out",
  "roleplay-act_it_out_listening",
  "roleplay-act_it_out_speaking",
  "roleplay-act_it_out_listening_speaking",
];

export const formatProgressItem = async (progress) => {
  const {
    memberId,
    courseId,
    sectionId,
    topicId,
    roleplayId,
    botId,
    exercise,
  } = progress;
  const memberResponse = await memoizeGetUserDetails(memberId);
  const member = memberResponse.UserAttributes || {};

  try {
    const isIndependentExercise =
      courseId && sectionId && topicId
        ? courseId.includes("none-") ||
          sectionId.includes("none-") ||
          topicId.includes("none-")
        : false;

    let topicResponse;
    if (courseId && sectionId && topicId && !isIndependentExercise) {
      topicResponse = await memoizeGetCourseSectionTopic(
        courseId,
        sectionId,
        topicId,
      );
    } else {
      topicResponse = {};
    }
    const topic = topicResponse.Item || {};
    if (exercise === "roleplay") {
      const roleplayResponse = await memoizeGetRoleplay(roleplayId);
      const roleplay = roleplayResponse.Item || {};
      return {
        ...progress,
        preferred_username: member.preferred_username,
        description: topic.description || "",
        topicName: topic.topicName || "topic_name_not_known",
        roleplayName: roleplay.roleplayName || "roleplay_name_not_known",
      };
    }
    if (isBotExercise(exercise)) {
      const botResponse = await memoizeGetBot(botId);
      const bot = botResponse.Item || {};
      return {
        ...progress,
        description: topic.description || "",
        preferred_username: member.preferred_username,
        topicName: topic.topicName || "topic_name_not_known",
        botName: bot.botName || "bot_name_not_known",
      };
    }

    return {
      ...progress,
      description: topic.description || "",
      preferred_username: member.preferred_username,
      topicName: topic.topicName || "topic_name_not_known",
    };
  } catch (error) {
    if (exercise === "roleplay") {
      return {
        ...progress,
        preferred_username: member.preferred_username,
        topicName: "topic_name_not_known",
        roleplayName: "roleplay_name_not_known",
      };
    }
    if (isBotExercise(exercise)) {
      return {
        ...progress,
        preferred_username: member.preferred_username,
        topicName: "topic_name_not_known",
        botName: "bot_name_not_known",
      };
    }

    return {
      ...progress,
      preferred_username: member.preferred_username,
      topicName: "topic_name_not_known",
    };
  }
};

export const formatRoleplayGames = (games) => {
  const newGameArray = [{}, {}, {}, {}, {}, {}];
  for (let i = 0; i < games.length; i += 1) {
    const gameIndex = gameList.indexOf(games[i].exercise);
    newGameArray[gameIndex] = games[i];
  }
  return newGameArray;
};

export const compareRoleplayGames = (a, b) => {
  if (gameList.indexOf(a.exercise) < gameList.indexOf(b.exercise)) {
    return -1;
  }
  if (gameList.indexOf(a.exercise) > gameList.indexOf(b.exercise)) {
    return 1;
  }
  return 0;
};

export const formatProgress = async (progress = []) =>
  Promise.all(progress.map(formatProgressItem));

export const invalidateProgressCache = async (progress) => {
  const nonIdStringCompare = "none";
  const {
    courseId,
    sectionId,
    topicId,
    memberId,
    roleplayId,
    botId,
    exercise,
  } = progress;

  const isTopic =
    courseId &&
    !courseId.includes(nonIdStringCompare) &&
    sectionId &&
    !sectionId.includes(nonIdStringCompare) &&
    topicId &&
    !topicId.includes(nonIdStringCompare);

  if (isTopic) {
    await invalidateCache(DataType.TOPIC_PROGRESS, {
      courseId,
      sectionId,
      topicId,
      memberId,
      exercise,
    });
  } else {
    if (isBotExercise(exercise)) {
      await invalidateCache(DataType.PROGRESS_BOT, {
        memberId,
        botId,
        exercise,
      });
    }

    if (exercise === "roleplay") {
      await invalidateCache(DataType.PROGRESS_ROLEPLAY, {
        memberId,
        roleplayId,
        exercise,
      });
    }
  }
};
